// HIA.1 — Prompt inline de feedback contextual do Consultor IA.
//
// Não interrompe: rende como card no rodapé do drawer/card, uma pergunta
// principal + opcionalmente um comentário. Deduplica por trigger+projeto
// via sessionStorage.

import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { submitHomologationFeedback } from "@/lib/ai/homologation.functions";

const RATINGS: Array<{ key: "very_useful" | "useful" | "slightly_useful" | "not_useful"; label: string }> = [
  { key: "very_useful", label: "Muito útil" },
  { key: "useful", label: "Útil" },
  { key: "slightly_useful", label: "Pouco útil" },
  { key: "not_useful", label: "Não foi útil" },
];

const EXIT_OPTIONS: Array<{ key: "yes" | "partial" | "no"; label: string }> = [
  { key: "yes", label: "Sim" },
  { key: "partial", label: "Parcialmente" },
  { key: "no", label: "Não" },
];

type Trigger =
  | "first_applied" | "third_use" | "section_completed"
  | "review_closed" | "repeat_regeneration" | "critical_conflict" | "exit_survey";

export function useHomologationFeedbackDedupe(trigger: Trigger, projectId: string | null) {
  const key = `p101.hia1.fb.${trigger}.${projectId ?? "none"}`;
  const already = typeof window !== "undefined" && sessionStorage.getItem(key) === "1";
  const markDone = () => {
    try { if (typeof window !== "undefined") sessionStorage.setItem(key, "1"); } catch { /* noop */ }
  };
  return { already, markDone };
}

export function HomologationFeedbackPrompt({
  trigger,
  projectId,
  question,
  variant = "rating",
  context,
  onDone,
}: {
  trigger: Trigger;
  projectId: string | null;
  question?: string;
  variant?: "rating" | "exit";
  context?: Record<string, unknown>;
  onDone?: () => void;
}) {
  const submit = useServerFn(submitHomologationFeedback);
  const { markDone } = useHomologationFeedbackDedupe(trigger, projectId);
  const [rating, setRating] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const options = variant === "exit" ? EXIT_OPTIONS : RATINGS;
  const finalQuestion = question ?? (variant === "exit" ? "Encontrou o que precisava?" : "Esta ajuda foi útil?");

  async function send(r: string, c?: string) {
    setSubmitting(true);
    try {
      const res: any = await submit({ data: { trigger, rating: r as any, comment: c || null, projectId, context } });
      if (res?.ok || res?.skipped) {
        markDone();
        setDone(true);
        onDone?.();
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível enviar.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-lg border border-black/5 bg-black/[0.02] p-3 text-[11px] text-[var(--gray-ink)]">
        Obrigado — o teu feedback ajuda a melhorar o Consultor.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-black/5 bg-white p-3">
      <p className="text-[12px] font-semibold text-[var(--navy)]">{finalQuestion}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => { setRating(o.key); if (variant === "exit") void send(o.key); }}
            disabled={submitting}
            className={`rounded-md border px-2.5 py-1 text-[11px] font-semibold transition ${
              rating === o.key
                ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                : "border-black/10 bg-white text-[var(--navy)] hover:bg-black/[0.03]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {variant === "rating" && rating && (
        <div className="mt-2 space-y-1.5">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="O que poderia melhorar? (opcional)"
            className="w-full resize-none rounded-md border border-black/10 bg-white p-2 text-[11px] text-[var(--navy)]"
            rows={2}
          />
          <div className="flex justify-end gap-1.5">
            <button
              onClick={() => void send(rating)}
              disabled={submitting}
              className="rounded-md border border-black/10 bg-white px-3 py-1 text-[11px] font-semibold text-[var(--navy)]"
            >
              Enviar sem comentário
            </button>
            <button
              onClick={() => void send(rating, comment)}
              disabled={submitting}
              className="rounded-md bg-[var(--navy)] px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-60"
            >
              Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
